/**
 * Cartridge selector — vertical list MVP (sc-2818).
 *
 * After cabinet boot, the cabinet renders this screen on the canvas (NOT
 * DOM — we stay inside the bezel for the proper arcade feel). The player
 * navigates with arrow keys + Z/Enter; the selector resolves with the
 * chosen `GameModule`, then the cabinet hands off to `launchCartridge`.
 *
 * MVP: vertical stack of cartridge titles, accent dot per-tile, one tile
 * focused. Grid layout, thumbnails, gamepad/touch nav, and animated
 * marquee tagline are all followups under epic 2740.
 *
 * Lifecycle:
 *   - `runSelector(ctx2d, input, cartridges)` starts the selection loop
 *   - It polls input via rAF, advances/retreats the focus on
 *     `up`/`down`/`left`/`right` justPressed
 *   - On `start`/`fireA` justPressed: resolves with the focused module
 *   - On `coin` justPressed: cosmetic — could fire a coin SFX once audio
 *     is wired
 *
 * Determinism note: the selector is NOT part of any replay. PRNG seeding
 * happens inside the launcher, after the player picks a cartridge.
 */

import type { GameModule } from '@arcraz/arcadia-types';
import type { InputSource } from './loop.js';

const WIDTH = 480;
const HEIGHT = 270;

const SKY_TOP = '#0a0418';
const SKY_HORIZON = '#5a1a78';
const TITLE_YELLOW = '#ffea00';
const SUBTITLE_CYAN = '#00d9ff';
const TILE_DIM = 'rgba(255,234,0,0.35)';
const TILE_BORDER_FOCUSED = '#ffea00';
const TILE_BORDER_DIM = '#5a1a78';
const TILE_BG_DIM = 'rgba(20, 6, 40, 0.55)';
const TILE_BG_FOCUSED = 'rgba(40, 10, 60, 0.85)';
const FOOTER = '#7af3c8';

const TILE_HEIGHT = 30;
const TILE_GAP = 6;
const TILES_TOP = 78;

export interface SelectorResult {
  module_: GameModule;
}

/**
 * Run the selector until the player picks a cartridge.
 * Returns the chosen GameModule.
 */
export function runSelector(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  cartridges: readonly GameModule[]
): Promise<SelectorResult> {
  if (cartridges.length === 0) {
    return Promise.reject(new Error('Selector started with empty cartridge list.'));
  }

  let focusIndex = 0;
  let blink = 0;

  return new Promise<SelectorResult>((resolve) => {
    const tick = (): void => {
      // Pump input. The keyboard source updates its held set on DOM
      // events; calling snapshot() advances the edge-event diff so
      // justPressed/justReleased reflect the latest frame.
      const state = input.snapshot();

      if (state.justPressed.has('up') && focusIndex > 0) {
        focusIndex -= 1;
      } else if (state.justPressed.has('down') && focusIndex < cartridges.length - 1) {
        focusIndex += 1;
      }

      if (
        state.justPressed.has('start') ||
        state.justPressed.has('fireA')
      ) {
        const picked = cartridges[focusIndex] as GameModule;
        resolve({ module_: picked });
        return;
      }

      blink = (blink + 1) % 60;
      render(ctx2d, cartridges, focusIndex, blink);
      requestAnimationFrame(tick);
    };

    tick();
  });
}

function render(
  g: CanvasRenderingContext2D,
  cartridges: readonly GameModule[],
  focusIndex: number,
  blink: number
): void {
  // Background: same vaporwave gradient as the cartridges, so the
  // selector feels like the cabinet's "home screen" not a separate
  // mode.
  const grad = g.createLinearGradient(0, 0, 0, HEIGHT);
  grad.addColorStop(0, SKY_TOP);
  grad.addColorStop(0.7, SKY_HORIZON);
  grad.addColorStop(1, SKY_TOP);
  g.fillStyle = grad;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Selector title. (No "ARCADIA AMUSEMENTS INC." subtitle here — it
  // already sits in the cabinet marquee above the canvas, so duplicating
  // it inside the canvas reads as redundant.)
  g.textAlign = 'center';
  g.textBaseline = 'top';
  g.fillStyle = TITLE_YELLOW;
  g.font = 'bold 16px ui-monospace, monospace';
  g.fillText('SELECT YOUR GAME', WIDTH / 2, 28);

  // Tile list.
  const tileX = 60;
  const tileW = WIDTH - 120;

  for (let i = 0; i < cartridges.length; i++) {
    const m = cartridges[i] as GameModule;
    const tileY = TILES_TOP + i * (TILE_HEIGHT + TILE_GAP);
    const focused = i === focusIndex;

    // Tile background.
    g.fillStyle = focused ? TILE_BG_FOCUSED : TILE_BG_DIM;
    g.fillRect(tileX, tileY, tileW, TILE_HEIGHT);

    // Tile border.
    g.strokeStyle = focused ? TILE_BORDER_FOCUSED : TILE_BORDER_DIM;
    g.lineWidth = focused ? 2 : 1;
    g.strokeRect(tileX + 0.5, tileY + 0.5, tileW - 1, TILE_HEIGHT - 1);

    // Accent dot from paletteHint (cartridge's signature color).
    const accent = m.paletteHint[0] ?? TITLE_YELLOW;
    g.fillStyle = accent;
    g.fillRect(tileX + 8, tileY + TILE_HEIGHT / 2 - 4, 8, 8);

    // Title.
    g.textAlign = 'left';
    g.textBaseline = 'middle';
    g.font = 'bold 11px ui-monospace, monospace';
    g.fillStyle = focused ? TITLE_YELLOW : TILE_DIM;
    g.fillText(m.title, tileX + 24, tileY + TILE_HEIGHT / 2 - 1);

    // Genre tag on the right.
    g.textAlign = 'right';
    g.font = '8px ui-monospace, monospace';
    g.fillStyle = focused ? SUBTITLE_CYAN : 'rgba(0, 217, 255, 0.35)';
    g.fillText(m.genre.toUpperCase(), tileX + tileW - 12, tileY + TILE_HEIGHT / 2 - 1);

    // Focused indicator (left chevron, blinks).
    if (focused && blink < 40) {
      g.textAlign = 'left';
      g.fillStyle = TITLE_YELLOW;
      g.font = 'bold 10px ui-monospace, monospace';
      g.fillText('>', tileX - 12, tileY + TILE_HEIGHT / 2 - 1);
    }
  }

  // Footer hint. Positioned ABOVE the area where the PWA install banner
  // floats in attract mode (the banner sits at the canvas-bottom region
  // when the install funnel is active). Putting the hint at HEIGHT - 50
  // keeps it visible regardless of banner state.
  g.textAlign = 'center';
  g.textBaseline = 'bottom';
  g.font = '8px ui-monospace, monospace';
  g.fillStyle = FOOTER;
  if (blink < 45) {
    g.fillText('ARROWS / Z TO PICK', WIDTH / 2, HEIGHT - 50);
  }

  // Reset text alignment defaults (cartridge rendering relies on these).
  g.textAlign = 'left';
  g.textBaseline = 'top';
}
